import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, type User } from "./api";
import { canAccessModule } from "./lib/permissions";
import {
  DEFAULT_FEATURES,
  normalizeFeatures,
  type FeatureFlags,
  type FeatureName,
} from "./lib/features";
import { useSessionCloseLogout } from "./hooks/useSessionCloseLogout";

interface AuthCtx {
  user: User | null;
  ready: boolean;
  features: FeatureFlags;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  refreshFeatures: () => Promise<void>;
  can: (action: string) => boolean;
  canAccess: (moduleKey: string) => boolean;
  isFeatureEnabled: (feature: FeatureName) => boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [features, setFeatures] = useState<FeatureFlags>(DEFAULT_FEATURES);

  // Load the deployment's feature flags. Failures fall back to defaults (all
  // ON) — the API is the authoritative gate so an optimistic default is safe.
  const refreshFeatures = useCallback(async () => {
    try {
      const data = await api.get<Partial<FeatureFlags>>("/features");
      setFeatures(normalizeFeatures(data));
    } catch {
      setFeatures(DEFAULT_FEATURES);
    }
  }, []);

  // Auth state is now derived from the httpOnly session cookie. On load (and on
  // demand) we ask the API who we are; a 401 simply means no valid cookie.
  const refresh = useCallback(async () => {
    try {
      const data = await api.get<{ user: User }>("/auth/me");
      setUser(data.user);
      await refreshFeatures();
    } catch {
      setUser(null);
    }
  }, [refreshFeatures]);

  useEffect(() => {
    refresh().finally(() => setReady(true));
  }, [refresh]);

  // Sign out when the browser closes (session cookie) or the last app tab
  // closes. Reload / back-forward is ignored so refresh does not log out.
  useSessionCloseLogout(!!user);

  const login = useCallback(
    async (username: string, password: string) => {
      // The API sets the session cookie on this response; we only need the user.
      const data = await api.post<{ user: User }>(
        "/auth/login",
        { username, password },
        false,
      );
      setUser(data.user);
      await refreshFeatures();
    },
    [refreshFeatures],
  );

  const logout = useCallback(async () => {
    // Clear local state immediately for a snappy sign-out, then revoke the
    // session server-side (bumps session_version + clears the cookie). The
    // server call is best-effort; a network failure must not strand the UI.
    setUser(null);
    setFeatures(DEFAULT_FEATURES);
    try {
      await api.post("/auth/logout");
    } catch {
      /* best-effort: local state is already cleared */
    }
  }, []);

  const can = useCallback(
    (action: string) => {
      if (!user) return false;
      return user.permissions.includes(action);
    },
    [user],
  );

  const canAccess = useCallback(
    (moduleKey: string) => {
      if (!user) return false;
      return canAccessModule(moduleKey, user.permissions);
    },
    [user],
  );

  const isFeatureEnabled = useCallback(
    (feature: FeatureName) => features[feature] !== false,
    [features],
  );

  const value = useMemo(
    () => ({
      user,
      ready,
      features,
      login,
      logout,
      refresh,
      refreshFeatures,
      can,
      canAccess,
      isFeatureEnabled,
    }),
    [
      user,
      ready,
      features,
      login,
      logout,
      refresh,
      refreshFeatures,
      can,
      canAccess,
      isFeatureEnabled,
    ],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside provider");
  return v;
}
